import { flow, identity, pipe } from 'fp-ts/function';
import { array, record, nonEmptyArray, option } from 'fp-ts';
import { createSelector } from 'reselect';
import { composeProps, createPropsExtender } from '@mews/framework';
import { getTextKeyFromServiceOrderState } from '@mews-commander/utils';
import { getSlotsByIds } from '@mews-commander/legacy-entities/src/ducks/resourceSlots';
import { getResourceCategoryAssignmentsByResourceIds } from '@mews-commander/legacy-entities/src/ducks/resourceCategoryAssignments';
import { getResourceCategoriesByIds } from '@mews-commander/legacy-entities/src/ducks/resourceCategories';
import { getCurrentCompanionAccommodationExtent } from '@mews-commander/legacy-entities/src/selectors/reservations';
import { mapSlotIds, mapRequestedCategoryIds, mapAssignedResourceIds } from '../utils';
import { GroupingTextKeys } from '../dateGroups';
import {
    getCheckedReservations,
    getServiceCheckedReservations,
    getNonCancelledCheckedReservations,
    getServiceNonCancelledCheckedReservations,
} from '../reservationModuleState';
import { getServiceSelectedReservationsDefaultProductOrders } from './reservationsData';

export const getTimeCount = createSelector(
    getServiceSelectedReservationsDefaultProductOrders,
    flow(array.filterMap(option.fromNullableK(({ startUtc }) => startUtc)), (dates) => new Set(dates).size)
);

const selectedServiceReservationsAssignedResourceIds = createSelector(
    getServiceNonCancelledCheckedReservations,
    mapAssignedResourceIds
);

const getServiceCategoryAssignemts = composeProps(
    getResourceCategoryAssignmentsByResourceIds,
    createPropsExtender('resourceIds', selectedServiceReservationsAssignedResourceIds)
);

const getServiceAssignedResourceCategoryIds = createSelector(
    getServiceCategoryAssignemts,
    array.map((assignment) => assignment.categoryId)
);

const getServiceAllAssignedResourceCategories = composeProps(
    getResourceCategoriesByIds,
    createPropsExtender('ids', getServiceAssignedResourceCategoryIds)
);
const getServiceCategoryIds = createSelector(
    getServiceAllAssignedResourceCategories,
    array.map((resourceCategory) => resourceCategory.id)
);

const getServiceSelectedReservationSlotIds = createSelector(getServiceNonCancelledCheckedReservations, mapSlotIds);

const getServiceSelectedInquredReservationCategoryIds = createSelector(
    getServiceNonCancelledCheckedReservations,
    mapRequestedCategoryIds
);

const getServiceSlotCategoryIds = createSelector(
    composeProps(getSlotsByIds, createPropsExtender('ids', getServiceSelectedReservationSlotIds)),
    array.map((slot) => slot.categoryId)
);

export const getServiceSelectedReservationsCategoryIds = createSelector(
    getServiceSlotCategoryIds,
    getServiceCategoryIds,
    getServiceSelectedInquredReservationCategoryIds,
    (slotCategoryIds, assignedResourceCategoryIds, unassignedResourceCategoryIds) => [
        ...slotCategoryIds,
        ...assignedResourceCategoryIds,
        ...unassignedResourceCategoryIds,
    ]
);

export const getServiceResourceCountsMap = flow(
    getServiceSelectedReservationsCategoryIds,
    nonEmptyArray.groupBy(identity),
    record.map(array.size)
);

export const getServiceGroupedReservationStates = createSelector(
    getServiceCheckedReservations,
    getCurrentCompanionAccommodationExtent,
    (reservations, companionExtent) =>
        pipe(
            reservations,
            array.map(({ serviceOrderState, reservationTimeState, options }) =>
                getTextKeyFromServiceOrderState(serviceOrderState, reservationTimeState, companionExtent, options)
            ),
            nonEmptyArray.groupBy(identity),
            identity<Record<GroupingTextKeys, GroupingTextKeys[]>>,
            record.map(array.size)
        )
);

export const selectedReservationsGroupedByRateId = createSelector(
    getNonCancelledCheckedReservations,
    nonEmptyArray.groupBy((r) => r.rateId)
);

const selectedServiceReservationsGroupedByRateId = createSelector(
    getServiceNonCancelledCheckedReservations,
    nonEmptyArray.groupBy((r) => r.rateId)
);

export const getServiceSelectedReservationsRateIds = flow(selectedServiceReservationsGroupedByRateId, record.keys);

export const getCheckedReservationsCumulatedOccupancyData = flow(
    getCheckedReservations,
    array.map((reservation) => reservation.occupancyData ?? []),
    array.flatten,
    (occupancyData) =>
        array.isNonEmpty(occupancyData)
            ? pipe(
                  occupancyData,
                  nonEmptyArray.groupBy(({ ageCategoryId }) => ageCategoryId),
                  record.mapWithIndex((ageCategoryId, data) =>
                      pipe(
                          data,
                          array.reduce({ ageCategoryId, personCount: 0 }, (acc, { personCount }) => {
                              acc.personCount += personCount;
                              return acc;
                          })
                      )
                  )
              )
            : null
);

export * from './summary';
export * from './guestsDialog';
export * from './itemsOverview';
export * from './reservationsData';
export * from './types';
